import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import fr from "./locales/fr.json";
import es from "./locales/es.json";
import pt from "./locales/pt.json";

/**
 * i18n bootstrap — single init at app load. We keep the resource bundles
 * static (imported, not lazy-loaded) for now because all four locales
 * combined are well under 40KB gzipped. Lazy-load can come once we add
 * a fifth locale or per-page namespacing.
 *
 * Detection order: explicit user preference on `users.locale` (synced
 * into localStorage when auth resolves) → localStorage → navigator
 * language → fallback English.
 */
export const SUPPORTED_LOCALES = ["en", "fr", "es", "pt"];
export const LOCALE_STORAGE_KEY = "trubeaute_locale";

export const LOCALE_LABELS = {
  en: "English",
  fr: "Français",
  es: "Español",
  pt: "Português",
};

/**
 * URL-prefix locale detection — synchronously inspects the current
 * pathname for `/fr/...`, `/es/...`, or `/pt/...` and surfaces the
 * locale so the SPA boots in the right language without a render
 * flash. This is what the build-time SSR prerender script relies on:
 * a crawler hitting `/pt/about` gets a Portuguese snapshot AND the
 * hydrated SPA stays in Portuguese because i18next is already on
 * `pt` before React renders.
 */
export function detectUrlPrefixLocale() {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname || "/";
  const m = path.match(/^\/(fr|es|pt)(\/|$)/);
  return m ? m[1] : null;
}

const urlLocale = detectUrlPrefixLocale();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
      es: { translation: es },
      pt: { translation: pt },
    },
    // When the URL itself encodes a locale (/fr/..., /es/...) we honour
    // that ahead of localStorage / navigator — otherwise a French user
    // who once visited the English site would never see their snapshot.
    lng: urlLocale || undefined,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES,
    // Locale codes we don't ship (e.g. fr-CA, es-MX, pt-PT) collapse onto
    // the base language so a Quebecois / Portugal user still gets their
    // base language rather than English (with pt-PT falling onto our
    // Brazilian Portuguese bundle — better than nothing).
    load: "languageOnly",
    nonExplicitSupportedLngs: true,
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
    },
    interpolation: {
      // React already escapes by default — double-escaping breaks
      // legitimate apostrophes in French copy.
      escapeValue: false,
    },
    returnNull: false,
  });

export default i18n;
